import { CombinedError } from "@urql/core";
import { GraphQLError } from "graphql";
import { map, pipe } from "wonka";
export const defaultUrqlErrorMessageRewriter = (message) => message
    // URQL CombinedError.message prefixes each "section" with bracketed tags.
    // This regex removes them at the beginning of the string AND after newlines.
    .replace(/(^|\n)\[(GraphQL|Network)\]\s*/gi, "$1")
    // Other common prefixes seen in app-level error plumbing/logging
    .replace(/^GraphQL error:\s*/i, "")
    .replace(/^Network error:\s*/i, "");
function rewriteGraphQLErrorMessage(err, rewrite) {
    const newMessage = rewrite(err.message);
    if (newMessage === err.message)
        return err;
    return new GraphQLError(newMessage, {
        nodes: err.nodes,
        source: err.source,
        positions: err.positions,
        path: err.path,
        originalError: err.originalError,
        extensions: err.extensions
    });
}
function rewriteNetworkErrorMessage(err, rewrite) {
    const newMessage = rewrite(err.message);
    if (newMessage === err.message)
        return err;
    // Preserve the error name; message is what the UI typically shows.
    const next = new Error(newMessage);
    next.name = err.name;
    return next;
}
export function rewriteCombinedError(error, rewrite = defaultUrqlErrorMessageRewriter) {
    const nextGraphQLErrors = (error.graphQLErrors || []).map((e) => rewriteGraphQLErrorMessage(e, rewrite));
    const nextNetworkError = error.networkError
        ? rewriteNetworkErrorMessage(error.networkError, rewrite)
        : undefined;
    // If nothing changed, keep reference identity.
    const graphQLErrorsChanged = nextGraphQLErrors.length !== error.graphQLErrors.length ||
        nextGraphQLErrors.some((e, i) => e !== error.graphQLErrors[i]);
    const networkErrorChanged = nextNetworkError !== error.networkError;
    const combinedMessageChanged = rewrite(error.message) !== error.message;
    if (!graphQLErrorsChanged &&
        !networkErrorChanged &&
        !combinedMessageChanged)
        return error;
    const next = new CombinedError({
        graphQLErrors: nextGraphQLErrors,
        networkError: nextNetworkError,
        response: error.response
    });
    // Important: URQL adds "[GraphQL]" / "[Network]" prefixes in CombinedError.message,
    // so we must rewrite the *combined* message as well for UIs that display `error.message`.
    const rewrittenMessage = rewrite(next.message);
    if (rewrittenMessage !== next.message) {
        next.message = rewrittenMessage;
    }
    return next;
}
/**
 * Exchange that rewrites backend errors before they reach UI hooks/components.
 *
 * Usage:
 * - Put it to the LEFT of `fetchExchange` / `subscriptionExchange` so it sees their results.
 * - Provide any transform (including `.replace(...)`) to normalize backend messages for users.
 */
export function createUrqlErrorRewriteExchange(rewrite = defaultUrqlErrorMessageRewriter) {
    return ({ forward }) => (ops$) => pipe(forward(ops$), map((result) => {
        if (!result.error)
            return result;
        return {
            ...result,
            error: rewriteCombinedError(result.error, rewrite)
        };
    }));
}
